import { Controller } from "@hotwired/stimulus"

// Drives the Make -> Model -> Year cascade on the vehicle form and applies the
// catalog's spec defaults.
//
// Prefill is SILENT and UNCONDITIONAL: picking a model overwrites every
// catalog-derived field, the display name included. There is no touched-state
// tracking, so re-picking a model is treated as "I picked the wrong car",
// not "keep my edits". This is a deliberate design decision.
export default class extends Controller {
  static targets = [
    "make", "model", "year", "trim", "name",
    "category", "seats", "luggage", "fuelType", "transmission",
    "amenity", "customFields"
  ]

  static values = {
    catalog: Array,
    // Server-rendered current selection, so editing a car (or re-rendering
    // after a validation error) shows what's actually stored.
    selectedMake: String,
    selectedModel: String,
    selectedYear: String
  }

  connect() {
    this.populateMakes()
    this.restoreSelection()
  }

  populateMakes() {
    this.fill(this.makeTarget, [{ value: "", label: "Select a make" }].concat(
      this.catalogValue.map((make) => ({ value: String(make.id), label: make.name }))
    ))
    this.modelTarget.disabled = true
  }

  // Rebuilds the cascade to match the saved record WITHOUT calling applySpec or
  // composeName — re-applying the catalog defaults here would silently discard
  // the per-unit spec and display name the operator already set.
  restoreSelection() {
    if (!this.selectedMakeValue) return

    this.makeTarget.value = this.selectedMakeValue
    const make = this.selectedMake()
    if (!make) return

    this.modelTarget.disabled = false
    this.fill(this.modelTarget, [{ value: "", label: "Select a model" }]
      .concat(make.models.map((m) => ({ value: String(m.id), label: this.modelLabel(m) })))
      .concat([{ value: "custom", label: "+ Can't find it? Add your own" }]))

    if (this.selectedModelValue) {
      this.modelTarget.value = this.selectedModelValue
      const model = this.selectedModel()
      if (model) this.fillYears(model)
    }

    if (this.selectedYearValue) this.yearTarget.value = this.selectedYearValue
  }

  fillYears(model) {
    this.fill(this.yearTarget, [{ value: "", label: "Year" }].concat(
      model.years.map((y) => ({ value: String(y), label: String(y) }))
    ))
  }

  makeChanged() {
    const make = this.selectedMake()
    this.modelTarget.disabled = !make

    const options = [{ value: "", label: "Select a model" }].concat(
      make ? make.models.map((m) => ({ value: String(m.id), label: this.modelLabel(m) })) : []
    )
    options.push({ value: "custom", label: "+ Can't find it? Add your own" })

    this.fill(this.modelTarget, options)
    this.toggleCustom(false)
  }

  modelChanged() {
    if (this.modelTarget.value === "custom") {
      this.toggleCustom(true)
      return
    }

    this.toggleCustom(false)
    const model = this.selectedModel()
    if (!model) return

    this.fillYears(model)
    this.applySpec(model)
    this.composeName()
  }

  composeName() {
    const make = this.selectedMake()
    const model = this.selectedModel()
    if (!make || !model) return

    const parts = [this.yearTarget.value, make.name, model.name, this.trimTarget.value]
    this.nameTarget.value = parts.filter((p) => p && p.trim()).join(" ")
  }

  applySpec(model) {
    this.categoryTarget.value = model.category
    this.seatsTarget.value = model.seats
    this.luggageTarget.value = model.luggageCapacity
    this.fuelTypeTarget.value = model.fuelType
    this.transmissionTarget.value = model.transmission

    this.amenityTargets.forEach((checkbox) => {
      checkbox.checked = model.amenities.includes(checkbox.value)
    })
  }

  toggleCustom(show) {
    this.customFieldsTarget.classList.toggle("hidden", !show)
    this.customFieldsTarget
      .querySelectorAll("input")
      .forEach((input) => { input.disabled = !show })

    // The "custom" sentinel lives in the select bound to vehicle_model_id.
    // Disable it while it holds the sentinel so the browser submits nothing
    // for that field — the server treats a non-numeric id as "no model", but
    // not sending it at all is the honest shape.
    this.modelTarget.disabled = show
  }

  selectedMake() {
    return this.catalogValue.find((m) => String(m.id) === this.makeTarget.value)
  }

  selectedModel() {
    const make = this.selectedMake()
    if (!make) return null
    return make.models.find((m) => String(m.id) === this.modelTarget.value)
  }

  modelLabel(model) {
    return model.trim ? `${model.name} ${model.trim}` : model.name
  }

  fill(select, options) {
    select.innerHTML = ""
    options.forEach(({ value, label }) => {
      const option = document.createElement("option")
      option.value = value
      option.textContent = label
      select.appendChild(option)
    })
  }
}
